import { useQuery } from '@tanstack/react-query';

import { type QueryFunction } from '@frontend/api';
import { keysToCamelCaseDeep } from '@frontend/utils';

type PageBoundingBox = {
  x1: number;
  y1: number;
  width: number;
  height: number;
};

export type TableCell = {
  boundingBox: PageBoundingBox | null;
  columnIndex: number;
  columnSpan: number;
  content: string | null;
  headerKey: number;
  spreadsheetBoundingBox: unknown | null;
};

export type TableRow = {
  boundingBox: PageBoundingBox | null;
  cells: TableCell[];
  spreadsheetBoundingBox: unknown | null;
};

export type BOMHeader = {
  row: TableRow;
  itemNumberCell: TableCell | null;
  descriptionCell: TableCell | null;
  partNumberCell: TableCell | null;
  quantityCell: TableCell | null;
  revisionCell: TableCell | null;
};

export type PartIdentifier = {
  partNumber: string | null;
  revision: string | null;
  description: string | null;
  metadata: Record<string, string> | null;
};

export type Table = {
  boundingBox: PageBoundingBox | null;
  spreadsheetBoundingBox: unknown | null;
  rows: TableRow[];
  headerRows: TableRow[];
  titleRows: TableRow[];
  pageNumber: number | null;
  confidence: number | null;
};

export type BOMRow = {
  partNumber: string | null;
  revision: string | null;
  description: string | null;
  count: number;
  children?: BOMRow[];
  itemNumber?: number;
};

export type ExtractedTableType = {
  table: Table;
  bomHeader: BOMHeader | null;
  bomRows: BOMRow[] | null;
  backupRootIdentifier: PartIdentifier | null;
};

type GetExtractedTableQueryKeyType = ['fetchTableData', string];

const getUseGetExtractedTableKey = (
  authenticatedUrl: string,
): GetExtractedTableQueryKeyType =>
  ['fetchTableData', authenticatedUrl] as const;

export const useGetExtractedTable: QueryFunction<
  ExtractedTableType | null,
  [string | undefined],
  GetExtractedTableQueryKeyType
> = (authenticatedUrl, { queryOptions } = {}) => {
  const queryKey = getUseGetExtractedTableKey(authenticatedUrl ?? '');

  return useQuery({
    ...queryOptions,
    queryKey: queryKey,
    queryFn: async ({ signal }) => {
      if (!authenticatedUrl) return null;

      const response = await fetch(authenticatedUrl, {
        method: 'GET',
        signal,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      return keysToCamelCaseDeep(await response.json()) as ExtractedTableType;
    },
  });
};
